import React, { useEffect, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelGet, panelPost, useAdminGet,
} from '../kit';

/**
 * Ocean · COSMOS matching: the OCEAN page's semantic "Relevant IPs found in
 * COSMOS" (Backend/ocean/cosmos_match.py — For You's engine: Pinecone
 * candidates + the search_v2 LLM rescore, 0-100), panel section "ocean_match"
 * (Backend/ocean/admin.py).
 *
 * What it shows: statements by state and by origin (a card / an admin =
 * interactive, ahead of everything; load re-scoring and precompute =
 * background, at most half the daily cap), today's jobs against the cap, LLM
 * calls, the settings in force, the index's technologies, this process's
 * queue, failures, recent runs, the Search-indexes "ocean" step, and every
 * statement with its AI-scored list next to the keyword list.
 * What it runs: precompute the most-cited statements, re-score one statement,
 * retry failed, re-score after a university load (the Search-indexes step),
 * refresh the index's technologies. All but the last cost LLM calls.
 */

const S = 'ocean_match';

const STATUS = ['all', 'done', 'empty', 'pending', 'running', 'failed'];
const ORIGIN = ['all', 'interactive', 'background'];
const ORIGIN_LABELS: Record<string, string> = { all: 'Any origin', interactive: 'Card / admin', background: 'Load / precompute' };
const STATUS_LABELS: Record<string, string> = {
  all: 'Any state', done: 'Matched', empty: 'Nothing ≥ 40', pending: 'Waiting', running: 'Running', failed: 'Failed',
};

const num = (v: any) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

const OceanMatching: React.FC = () => {
  const res = useAdminGet<any>(S, 'overview');
  const [status, setStatus] = useState('all');
  const [origin, setOrigin] = useState('all');
  const list = useAdminGet<any>(S, 'statements', { status, origin, limit: 500 }, [status, origin]);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [top, setTop] = useState('200');
  const [full, setFull] = useState(false);
  const [preview, setPreview] = useState<any | null>(null);

  const d = res.data || {};
  const c = d.counts || {};
  const r = d.runs || {};
  const s = d.settings || {};
  const b = d.budget || {};
  const q = d.queue || {};
  const job = d.admin_job;
  const perJob = d.per_job_llm_calls || 6;
  const live = (q.queued || 0) > 0 || (q.active || []).length > 0 || !!job?.running || (c.running || 0) > 0;

  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => { res.reload(); list.reload(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  const act = async (action: string, body: Record<string, any>, done: (r: any) => string) => {
    setBusy(action); setMsg(null);
    const out = await panelPost(S, action, body);
    setBusy('');
    if (out.error) { setMsg({ kind: 'error', text: out.error }); return; }
    setMsg({ kind: 'ok', text: done(out.data) });
    res.reload(); list.reload();
  };
  const openPreview = async (pid: string) => {
    setPreview({ pid, loading: true });
    const out = await panelGet(S, 'matches', { pid });
    setPreview(out.error ? { pid, error: out.error } : out.data);
  };

  if (res.loading && !res.data) return <Loading />;
  if (res.error) return <Msg kind="error">{res.error}</Msg>;

  const topN = Math.max(0, Math.min(20000, parseInt(top, 10) || 0));
  const engineOff = s.engine === 'off';
  const ix = d.index || {};
  const stepRuns = d.step?.runs || [];

  return (
    <div className="flex flex-col gap-4">
      {engineOff && (
        <Msg kind="error">Semantic matching is off on this server (OCEAN_MATCH_ENGINE=off): cards show keyword matches only.</Msg>
      )}
      <KPIRow>
        <KPI label="Statements scored" value={num((c.done || 0) + (c.empty || 0))}
             hint={`${num(c.done)} matched · ${num(c.empty)} with nothing ≥ ${s.match_min ?? 40}`} />
        <KPI label="Waiting" value={num(c.pending)}
             hint={`${num(c.pending_interactive)} card/admin · ${num(c.pending_background)} background · ${num(c.deferred)} held to tomorrow`} />
        <KPI label="Failed" value={num(c.failed)} hint={`${num(c.failed_gave_up)} gave up · ${num((c.failed || 0) - (c.failed_gave_up || 0))} will retry`} />
        <KPI label="Jobs today (UTC)" value={b.cap ? `${num(r.today)} / ${num(b.cap)}` : num(r.today)}
             hint={`background ${num(r.today_background)}${b.background_cap != null ? ` / ${num(b.background_cap)}` : ''}`} />
        <KPI label="LLM calls today" value={num(r.llm_calls_today)}
             hint={`${num(r.llm_calls_30d)} in 30 days · ≈ ${perJob}/statement${r.avg_seconds_30d ? ` · ${r.avg_seconds_30d} s each` : ''}`} />
        <KPI label="Technologies in the index" value={ix.missing ? 'no index' : num(ix.technologies)}
             hint={ix.cosmos_refreshed_at ? `technologies refreshed ${fmtDate(ix.cosmos_refreshed_at)}`
               : ix.built_at ? `built ${fmtDate(ix.built_at)}` : undefined} />
      </KPIRow>

      <Section title="Run" right={<Btn small onClick={() => { res.reload(); list.reload(); }}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100">
          Paid: every job runs For You's engine — about {perJob} LLM calls per statement. A statement is scored the
          first time someone opens its card. Background work (precompute, re-scoring after a load) waits behind card
          requests and may use at most {Math.round((s.background_share ?? 0.5) * 100)}% of the daily cap
          ({num(b.background_cap)} of {num(b.cap)} jobs); what does not fit runs the next day. A re-scored statement keeps
          showing its current list until the new one is written.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput value={top} onChange={setTop} type="number" placeholder="200" />
          <DangerBtn onConfirm={() => act('precompute', { top: topN }, x =>
            `Queued ${num(x.queued)} statements (~${num(x.estimated_llm_calls)} LLM calls${x.days_at_budget ? `, ~${x.days_at_budget} day(s) at the background budget` : ''}). Already-scored ones are skipped.`)}>
            {busy === 'precompute' ? 'Queueing…' : `Precompute the ${num(topN)} most-cited`}
          </DangerBtn>
          <Btn onClick={() => act('retry_failed', {}, x => `Re-queued ${num(x.requeued)} statements.`)}
               disabled={!!busy || !(c.failed || 0)}>Retry failed</Btn>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <DangerBtn onConfirm={() => act('after_load', { full }, () => 'Started — progress below.')}>
            {full ? 'Re-score every scored statement' : 'Re-score after a university load'}
          </DangerBtn>
          <label className="text-xs text-grayscale-1100 flex items-center gap-1.5">
            <input type="checkbox" checked={full} onChange={e => setFull(e.target.checked)} />
            every scored statement (not only those a new technology could match)
          </label>
          <Btn onClick={() => act('refresh_index', {}, () => 'Started — progress below.')} disabled={!!busy || !!job?.running}
               title="Free: copies the corpus's technologies into OCEAN's index; nothing is re-scored">
            Refresh technologies only
          </Btn>
        </div>
        <p className="text-xs text-grayscale-1100">
          "Re-score after a university load" is the same as the "OCEAN COSMOS re-score" step in System → Search indexes:
          refresh the index's technologies, drop matches to technologies that left, re-queue the statements a new or
          edited technology could match.
        </p>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {job && (
          <div className="rounded-lg border border-grayscale-300 p-3 text-xs flex flex-col gap-1">
            <div className="font-semibold text-grayscale-1300">
              {job.action} — {job.running ? 'running…' : job.status} <span className="font-normal text-grayscale-1100">
              (by {job.by}, {fmtDate(job.started_at)})</span>
            </div>
            {!job.running && job.result && (
              <div className="text-grayscale-1100">
                {job.result.error || job.result.note || [
                  job.result.added != null && `+${num(job.result.added)} technologies`,
                  job.result.removed != null && `−${num(job.result.removed)}`,
                  job.result.edited != null && `${num(job.result.edited)} edited`,
                  job.result.requeued != null && `${num(job.result.requeued)} statements re-queued (~${num(job.result.estimated_llm_calls)} LLM calls)`,
                ].filter(Boolean).join(' · ')}
              </div>
            )}
            {(job.log || []).length > 0 && (
              <pre className="whitespace-pre-wrap text-[11px] text-grayscale-1100 max-h-40 overflow-auto">{(job.log || []).join('\n')}</pre>
            )}
          </div>
        )}
      </Section>

      <Section title="Settings in force">
        <DataTable rows={[{
          engine: s.engine, daily_cap: s.daily_cap, background: `${Math.round((s.background_share ?? 0) * 100)}%`,
          candidates: s.candidates, kept_from: s.match_min, strong_from: s.strong, kept_max: s.keep,
          expansion: s.expand ? 'on' : 'off', workers: s.workers,
        }]} labels={{ daily_cap: 'Daily cap (jobs)', background: 'Background share', kept_from: 'Kept from',
                     strong_from: '"Strong" from', kept_max: 'Kept per statement', workers: 'Workers / process' }} />
        <p className="text-xs text-grayscale-1100">
          Set with OCEAN_MATCH_ENGINE, _DAILY_CAP, _BACKGROUND_PCT, _CANDIDATES, _MIN, _STRONG, _KEEP, _EXPAND,
          _WORKERS in the API's environment.
        </p>
      </Section>

      <Section title={`Search indexes step "ocean"`}>
        {stepRuns.length === 0 ? (
          <p className="text-sm text-grayscale-1100">
            {d.step?.note || 'Not run yet. System → Search indexes → tick "OCEAN COSMOS re-score (paid LLM)".'}
          </p>
        ) : (
          <DataTable rows={stepRuns.map((x: any) => ({
            started_at: x.started_at, by: x.started_by, run_status: x.status,
            step_status: x.result?.status || (x.status === 'running' ? 'waiting' : '—'),
            added: x.result?.added, removed: x.result?.removed, requeued: x.result?.requeued,
            llm_calls: x.result?.estimated_llm_calls, note: x.result?.note || x.result?.error || '',
          }))} labels={{ started_at: 'Started', llm_calls: 'LLM calls (est.)' }} />
        )}
      </Section>

      <Section title="Queue (this process)">
        <p className="text-xs text-grayscale-1100">
          {q.enabled === false ? 'OCEAN_MATCH_WORKER=0 or the engine is off: this process runs no jobs.'
            : q.started ? `${q.workers} worker(s) running.` : 'Workers start the first time OCEAN queues a statement in this process.'}
        </p>
        <DataTable rows={[...(q.active || []).map((a: any) => ({ state: 'running', pid: a.pid, priority: '', seconds: a.seconds })),
                          ...(q.items || []).map((a: any) => ({ state: 'queued', pid: a.pid, priority: a.priority, seconds: '' }))]}
                   columns={['state', 'pid', 'priority', 'seconds']} empty="Nothing queued in this process." />
      </Section>

      <Section title="Failures">
        <DataTable rows={d.failures || []} columns={['statement', 'company', 'trigger', 'attempts', 'error', 'retry', 'next_retry_at']}
                   labels={{ trigger: 'Origin', next_retry_at: 'Next retry' }} empty="No failures."
                   actions={row => <Btn small disabled={!!busy} onClick={() => openPreview(row.pid)}>Open</Btn>} />
      </Section>

      <Section title="Statements" right={
        <div className="flex gap-2">
          <Select value={status} onChange={setStatus} options={STATUS} labels={STATUS_LABELS} />
          <Select value={origin} onChange={setOrigin} options={ORIGIN} labels={ORIGIN_LABELS} />
        </div>
      }>
        {list.error ? <Msg kind="error">{list.error}</Msg> : (
          <DataTable rows={list.data?.rows || []}
                     columns={['statement', 'company', 'status', 'best', 'match_count', 'origin', 'matched_at', 'requested_at', 'error']}
                     labels={{ best: 'Best score', match_count: 'Matches', matched_at: 'Scored', requested_at: 'Queued' }}
                     searchable csvName="ocean_cosmos_statements" tableId="ocean_cosmos_statements"
                     empty="No statement has been queued yet — they are, the first time someone opens a problem card."
                     actions={row => (
                       <div className="flex gap-1">
                         <Btn small onClick={() => openPreview(row.pid)}>Matches</Btn>
                         <Btn small disabled={!!busy || row.status === 'running'}
                              onClick={() => act('rescore', { pid: row.pid }, () => 'Queued ahead of background work.')}>Re-score</Btn>
                       </div>
                     )} />
        )}
        {list.data && (
          <p className="text-xs text-grayscale-1100">
            Showing {num((list.data.rows || []).length)} of {num(list.data.total)} statements with a job (newest first).
          </p>
        )}
      </Section>

      {preview && (
        <Section title={preview.statement || preview.pid} right={<Btn small onClick={() => setPreview(null)}>Close</Btn>}>
          {preview.loading ? <Loading /> : preview.error ? <Msg kind="error">{preview.error}</Msg> : (
            <>
              <p className="text-xs text-grayscale-1100">
                {[preview.company, preview.industry, preview.country].filter(Boolean).join(' · ')} · {preview.pid}
                {preview.job ? ` · ${preview.job.status}${preview.job.matched_at ? `, scored ${fmtDate(preview.job.matched_at)}` : ''}`
                  : ' · never queued'}
              </p>
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-grayscale-1300">AI-scored (what the card shows)</div>
                  <DataTable rows={preview.semantic || []} columns={['rank', 'relevance', 'label', 'title', 'university', 'trl']}
                             empty="Not scored yet, or nothing reached the threshold." />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-grayscale-1300">Keyword matches (the fallback)</div>
                  <DataTable rows={preview.keyword || []} columns={['rank', 'title', 'university', 'country']}
                             empty="No keyword match." />
                </div>
              </div>
              <DataTable rows={preview.runs || []}
                         columns={['started_at', 'origin', 'status', 'candidates', 'matches', 'llm_calls', 'seconds', 'error']}
                         labels={{ started_at: 'Run started' }} empty="No runs for this statement." />
              <div>
                <Btn small disabled={!!busy} onClick={async () => {
                  await act('rescore', { pid: preview.pid }, () => 'Queued ahead of background work.');
                  openPreview(preview.pid);
                }}>Re-score this statement</Btn>
              </div>
            </>
          )}
        </Section>
      )}

      <Section title="Recent runs">
        <DataTable rows={d.recent_runs || []}
                   columns={['started_at', 'statement', 'origin', 'status', 'candidates', 'matches', 'llm_calls', 'seconds', 'error']}
                   labels={{ started_at: 'Started', matches: 'Kept' }} searchable csvName="ocean_cosmos_runs" empty="No runs yet." />
      </Section>
    </div>
  );
};

export default OceanMatching;
